import React, { Fragment, useMemo } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { groupBy, orderBy } from 'lodash/fp'
import { ChevronLeftIcon, ChevronRight, PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	SettingsTemplateActions,
	useSettingsTemplatesQuery,
} from '@/entities/settings-template'
import { UsernameById } from '@/entities/user'

import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import { LinkButton } from '@/shared/components/ui/link-button'
import { Separator } from '@/shared/components/ui/separator'
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from '@/shared/components/ui/sidebar'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { SettingsTemplateType } from '@/shared/generated'
import { useModals } from '@/shared/lib/modal-manager'
import { intlFormat } from '@/shared/tools/dates'

import BuilderBlockElement from './BuilderBlockElement'
import { useTemplateBuilderContext } from './TemplateBuilderProvider'

export default function TemplatesBuilderLeftPanel() {
	const { activeType } = useTemplateBuilderContext()

	return (
		<Sidebar
			collapsible='none'
			data-testid='builder-sidebar'
			className='w-[400px]! border-r fade-in'
		>
			<AnimatePresence mode='wait' initial={false}>
				{!activeType ? (
					<motion.div
						key='general'
						className='flex size-full flex-col'
						initial='in'
						animate='visible'
						exit='out'
						variants={{
							in: { opacity: 0, x: -20 },
							visible: { opacity: 1, x: 0 },
							out: { opacity: 0, x: -20 },
						}}
						transition={{
							duration: 0.1,
						}}
					>
						<SidabarGeneralContent />
					</motion.div>
				) : (
					<motion.div
						key={activeType}
						className='flex size-full flex-col'
						initial='in'
						animate='visible'
						exit='out'
						variants={{
							in: { opacity: 0, x: 20 },
							visible: { opacity: 1, x: 0 },
							out: { opacity: 0, x: 20 },
						}}
						transition={{
							duration: 0.1,
						}}
					>
						<SidabarEntityContent />
					</motion.div>
				)}
			</AnimatePresence>
		</Sidebar>
	)
}

function SidabarGeneralContent() {
	const { t } = useTranslation()

	const { setActiveType, meta, template } = useTemplateBuilderContext()

	return (
		<Fragment>
			<SidebarHeader className='border-b'>
				<div className='flex w-full items-center justify-between'>
					<div className='font-bold'>
						{t('settings.general.title', { defaultValue: 'General' })}
					</div>
					{template && <SettingsTemplateActions template={template} />}
				</div>
			</SidebarHeader>
			<SidebarContent className='flex w-full flex-col items-start pt-2'>
				<SidebarGroup className='border-b'>
					<SidebarGroupContent>
						<SidebarMenu>
							{[
								SettingsTemplateType.PricingCampaign,
								SettingsTemplateType.ScenarioBusinessConstraints,
								SettingsTemplateType.ScenarioStopList,
								SettingsTemplateType.ScenarioStrategy,
							].map((section) => (
								<SidebarMenuItem key={section}>
									<SidebarMenuButton
										type='button'
										onClick={() => setActiveType(section)}
									>
										<span className='font-medium'>
											{t(`template.type.${section}`, { defaultValue: section })}
										</span>
										<ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
			<SidebarFooter className='border-t'>
				<div className='mt-auto flex flex-col gap-3 p-3'>
					{meta?.created_by && (
						<section>
							<span className='text-sm font-medium text-muted-foreground'>
								{t('general_created')}:
							</span>
							<p className='flex items-center gap-1 text-sm font-medium'>
								{t('by')} <UsernameById userId={meta?.created_by} />,{' '}
								{meta?.created_at && intlFormat(new Date(meta?.created_at))}
							</p>
						</section>
					)}
					{meta?.updated_by && (
						<section>
							<span className='text-sm font-medium text-muted-foreground'>
								{t('general_last_updated')}:
							</span>
							<p className='flex items-center gap-1 text-sm font-medium'>
								{t('by')} <UsernameById userId={meta?.updated_by} />,{' '}
								{meta?.updated_at && intlFormat(new Date(meta?.updated_at))}
							</p>
						</section>
					)}
				</div>
			</SidebarFooter>
		</Fragment>
	)
}
function SidabarEntityContent() {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const { activeType, setActiveType } = useTemplateBuilderContext()
	const templatesQuery = useSettingsTemplatesQuery({
		offset: 0,
		limit: 1000,
		filters: [
			{
				name: 'template_type',
				operation: Operators.IN,
				value: [
					SettingsTemplateType.ScenarioBusinessConstraints,
					SettingsTemplateType.ScenarioStrategy,
					SettingsTemplateType.ScenarioStopList,
					SettingsTemplateType.PricingCampaign,
				],
			},
		],
	})
	const sectionsItems = useMemo(() => {
		const templates = templatesQuery.data?.data || []

		return orderBy(
			['created_at'],
			['desc'],
			groupBy('template_type', templates)[activeType as SettingsTemplateType]
		)
	}, [templatesQuery.data, activeType])

	return (
		<Fragment>
			<SidebarHeader className='border-b'>
				<div className='flex w-full items-center gap-2'>
					<Button
						variant='ghost'
						onClick={() => setActiveType(null)}
						size='icon-sm'
					>
						<ChevronLeftIcon />
					</Button>
					<Separator orientation='vertical' className='h-5' />
					<div className='font-bold'>
						{t(`template.type.${activeType}`, {
							defaultValue: activeType || '',
						})}
					</div>
				</div>
				<div className='flex items-center gap-2 text-sm'>
					<span className='text-muted-foreground'>
						{sectionsItems.length}{' '}
						{t('entity.templates', { defaultValue: 'Templates' })}
					</span>
					<Separator orientation='vertical' className='h-5' />
					<LinkButton
						onClick={() =>
							showModal('SETTINGS_TEMPLATE_MODAL', {
								template_type: activeType as SettingsTemplateType,
							})
						}
					>
						<PlusIcon />
						{t('general_create', { defaultValue: 'Create' })}
					</LinkButton>
				</div>
			</SidebarHeader>
			<SidebarContent className='flex w-full flex-col items-start'>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{templatesQuery.isPending ? (
								<>
									<Skeleton className='h-11 w-full' />
									<Skeleton className='h-11 w-full opacity-75' />
									<Skeleton className='h-11 w-full opacity-50' />
									<Skeleton className='h-11 w-full opacity-25' />
								</>
							) : (
								sectionsItems.map((template) => (
									<BuilderBlockElement key={template.id} block={template} />
								))
							)}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
		</Fragment>
	)
}
